import { LockableSelectItem, Pagination } from '@app/components'
import { currency, useExpenditures } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ActionIcon,
  Badge,
  Box,
  createStyles,
  Group,
  LoadingOverlay,
  Paper,
  type PaperProps,
  Select,
  Table,
  Text
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconDownload, IconEye } from '@tabler/icons-react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import { concat, isEmpty, map, omitBy, pick } from 'lodash'
import Link from 'next/link'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)

const useStyles = createStyles(() => ({
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  }
}))

export interface ExpendituresTableFiltersFormData {
  schoolYearId: string
  quarterId: string
  providerId: string
}

export interface ExpendituresTableProps extends Omit<PaperProps, 'children'> {
  initialFilters: ExpendituresTableFiltersFormData
  onFiltersChange: (filters: ExpendituresTableFiltersFormData) => void
}

export const ExpendituresTable: FC<ExpendituresTableProps> = ({ initialFilters, onFiltersChange, ...props }) => {
  const { classes } = useStyles()
  const sugar = useSugarContext()
  const [page, setPage] = useState(1)
  const filterForm = useForm<ExpendituresTableFiltersFormData>({
    initialValues: initialFilters
  })
  const { data: expenditures, isFetching: isFetchingExpenditures } = useExpenditures(
    page,
    omitBy(filterForm.values, isEmpty)
  )

  useEffect(() => {
    onFiltersChange({
      schoolYearId: filterForm.values.schoolYearId,
      quarterId: filterForm.values.quarterId,
      providerId: filterForm.values.providerId
    })
  }, [filterForm.values.providerId, filterForm.values.quarterId, filterForm.values.schoolYearId, onFiltersChange])

  const {
    onChange: onSchoolYearChange,
    value: schoolYearId,
    ...schoolYearProps
  } = filterForm.getInputProps('schoolYearId')
  const { value: quarterId, ...quarterProps } = filterForm.getInputProps('quarterId')
  const { value: providerId, ...providerProps } = filterForm.getInputProps('providerId')

  return (
    <Paper
      p='lg'
      {...props}
    >
      <Box pos='relative'>
        <LoadingOverlay visible={isFetchingExpenditures} />
        <Group
          align='center'
          grow
          mb='lg'
        >
          <Select
            clearable={!!filterForm.values.schoolYearId}
            data={useMemo(
              () =>
                concat(
                  { label: 'All School Years', value: '', locked: false },
                  map(sugar?.options?.expenditureReportingSchoolYears, (schoolYear) =>
                    pick(schoolYear, ['label', 'locked', 'value'])
                  )
                ),
              [sugar?.options?.expenditureReportingSchoolYears]
            )}
            itemComponent={LockableSelectItem}
            label='School Year'
            onChange={useCallback(
              (value: string) => {
                filterForm.setFieldValue('quarterId', '')
                onSchoolYearChange(value)
              },
              [filterForm, onSchoolYearChange]
            )}
            selectOnBlur
            value={schoolYearId || ''}
            {...schoolYearProps}
          />
          <Select
            clearable={!!filterForm.values.quarterId}
            data={useMemo(
              () =>
                concat(
                  { label: 'All Quarters', locked: false, value: '' },
                  filterForm.values.schoolYearId
                    ? map(
                        sugar?.getExpenditureReportingSchoolYearByValue(filterForm.values.schoolYearId)?.quarters,
                        (option) => pick(option, ['label', 'locked', 'value'])
                      )
                    : []
                ),
              [filterForm.values.schoolYearId, sugar]
            )}
            disabled={!filterForm.values.schoolYearId}
            itemComponent={LockableSelectItem}
            label='Quarter'
            selectOnBlur
            value={quarterId || ''}
            {...quarterProps}
          />
          {sugar?.options?.businessProviders?.length && (
            <Select
              clearable={!!filterForm.values.providerId}
              data={concat({ label: 'All Providers', value: '' }, sugar.options.businessProviders)}
              label='Provider'
              selectOnBlur
              value={providerId || ''}
              {...providerProps}
            />
          )}
        </Group>
        {expenditures?.data?.length ? (
          <>
            <Table
              mb='xl'
              horizontalSpacing='xs'
              verticalSpacing='xs'
            >
              <thead>
                <tr>
                  <th>Name</th>
                  <th className={classes.fitWidth}>Total</th>
                  <th className={classes.fitWidth}>Completed</th>
                  <th className={classes.fitWidth}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {map(expenditures?.data, (expenditure, idx) => (
                  <tr key={`expenditure-${idx}`}>
                    <td>
                      <Group>
                        <Text>{expenditure.name}</Text>
                        {expenditure.locked && <Badge color='red'>Locked</Badge>}
                      </Group>
                    </td>
                    <td className={classes.fitWidth}>{currency(expenditure.totalExpenditures)}</td>
                    <td className={classes.fitWidth}>
                      {expenditure.reportedCompleteDate ? dayjs(expenditure.reportedCompleteDate).format('lll') : '-'}
                    </td>
                    <td className={classes.fitWidth}>
                      <Group
                        align='center'
                        noWrap
                        position='center'
                        spacing={0}
                      >
                        <ActionIcon
                          color='navy'
                          component={Link}
                          href={`/expenditure-reports/report?providerId=${expenditure.providerId}&quarterId=${expenditure.quarterId}&schoolYearId=${expenditure.schoolYearId}`}
                          title='View'
                          variant='transparent'
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                        <ActionIcon
                          color='navy'
                          component='a'
                          href={`/api/expenditures/${expenditure.id}`}
                          title='Download'
                          variant='transparent'
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              onChange={setPage}
              total={expenditures?.pagination?.totalPages || 1}
              value={page}
            />
          </>
        ) : (
          <Group
            align='center'
            p='xl'
            position='center'
          >
            <Text
              color='dimmed'
              size='xl'
            >
              No Results
            </Text>
          </Group>
        )}
      </Box>
    </Paper>
  )
}

ExpendituresTable.displayName = 'ExpendituresTable'
